// parcaliGonder.js — dosyayı parçalara bölüp sırayla gönderir.
//
// NEDEN (bkz. .agent/MEDYA-YUKLEME-KUCULTME.md §3):
// Sunucunun önünde bizim kontrolümüzde OLMAYAN bir gövde boyutu sınırı var;
// değeri bilinmiyor, tahmin 5-10 MB, yarın değişebilir. Tek istekte gönderilen
// video ona takılıyor. Parçalara bölününce her istek küçük kalıyor ve sınır ne
// olursa olsun devre dışı kalıyor. İkinci kazanç: kötü 3G'de kopan bağlantı
// dosyayı baştan başlatmıyor, sadece o parça tekrarlanıyor.
//
// `413`E BEL BAĞLANMAZ (kullanıcı uyarısı, 23.09.2026 — yerinde bir uyarıydı).
// Sınırı uygulayan katman BE değil, aradaki nginx/proxy/WAF ve bunlar gövde
// sınırı aşılınca çoğu zaman düzgün bir 413 dönmez: nginx sıklıkla BAĞLANTIYI
// KAPATIR (tarayıcıya hiç HTTP durumu ulaşmaz), proxy'ler 502/503/520 dönebilir.
// Bu yüzden karar `413` görmeye değil, hatanın SINIFINA bakıyor.

// İstemcinin başlangıç parça boyutu. Bilinçli olarak düşük: "biraz yavaş ama
// çalışıyor", "hiç çalışmıyor"dan iyidir.
export const BASLANGIC_PARCA = 2 * 1024 * 1024;

// Bunun altına inilmez. Buraya kadar inip hâlâ olmuyorsa sorun boyut değildir.
export const EN_KUCUK_PARCA = 256 * 1024;

// Parça küçültmeden önce aynı boyutta kaç kez daha denenir.
// Kötü 3G'de kopma çoğu zaman GEÇİCİDİR; her kopmada parçayı küçültmek
// yüklemeyi gereksiz yere süründürür.
export const AYNI_BOYUT_DENEME = 2;

// Bu boyutun altındaki dosya parçalanmaz, tek istekle gider. Küçük bir resim
// için 3 istek atmanın anlamı yok; ayrıca mevcut ve denenmiş yol korunmuş olur.
export const PARCALAMA_ESIGI = BASLANGIC_PARCA;

/**
 * Hata boyuttan olabilir mi? SAF — test edilebilir.
 *
 * "Kesinlikle boyut değil" sınıfı dar tutuldu: yalnızca anlamı net olanlar.
 * Geri kalan HER ŞEY (durum kodu hiç gelmemesi dahil) küçültmeyi tetikler,
 * çünkü küçültmek ucuz, yanlış teşhis pahalı.
 */
export const kaliciHataMi = (durum) => [401, 403, 404, 405, 422].includes(Number(durum));

/** Bir sonraki parça boyutu. Yarıya iner, tabanın altına inmez. */
export const sonrakiParcaBoyutu = (mevcut, taban = EN_KUCUK_PARCA) => {
  const yeni = Math.floor(Number(mevcut) / 2);
  return yeni < taban ? null : yeni; // null = daha fazla küçültülemez
};

/** Dosya kaç parça eder? */
export const parcaSayisi = (dosyaBoyutu, parcaBoyutu) => {
  const b = Number(dosyaBoyutu);
  const p = Number(parcaBoyutu);
  if (!(b > 0) || !(p > 0)) return 0;
  return Math.ceil(b / p);
};

/** Bu dosya parçalanmalı mı? */
export const parcalanmaliMi = (dosya, esik = PARCALAMA_ESIGI) =>
  Boolean(dosya) && Number(dosya.size) > Number(esik);

// Iptal, hata DEGILDIR: kullanici bilerek durdurdu. Ayri bir tiple firlatiliyor
// ki parca kucultme/tekrar deneme mantigina yakalanmasin — aksi halde iptal
// "belirsiz hata" sayilip bosuna 4 kez daha denenirdi.
export class IptalEdildi extends Error {
  constructor() {
    super("Islem iptal edildi.");
    this.name = "IptalEdildi";
  }
}

export const iptalMi = (hata) => hata?.name === "IptalEdildi";

/** Bir isteğin durum kodunu çıkarır; hiç yoksa 0 (bağlantı koptu). */
export const durumKodu = (hata) =>
  Number(hata?.response?.status || hata?.status || 0);

/**
 * Dosyayı parçalara bölüp gönderir.
 *
 * @param {File} dosya
 * @param {{
 *   basla: (bilgi) => Promise<{id:string}>,
 *   parcaGonder: (id:number|string, sira:number, blob:Blob) => Promise<any>,
 *   bitir: (id:string) => Promise<any>,
 *   ilerleme?: (yuzde:number) => void,
 * }} tasiyici  HTTP burada DEĞİL — çağıran veriyor, böylece test edilebiliyor.
 */
export async function parcaliGonder(dosya, tasiyici) {
  let parcaBoyutu = BASLANGIC_PARCA;
  let toplam = parcaSayisi(dosya.size, parcaBoyutu);

  // DEVAM ETTIRME: sayfa yenilendikten sonra tarayici onceki dosyayi
  // okuyamiyor, kullanici dosyayi tekrar seciyor. Ama parcalar sunucuda
  // duruyor; AYNI dosyaysa gelmis parcalari atlayip kaldigi yerden devam
  // ediyoruz. Bulunamazsa normal akis — devam ettirme BEST EFFORT.
  let gelenler = new Set();
  let id = null;
  if (tasiyici.devam) {
    try {
      const d = await tasiyici.devam({ dosyaAdi: dosya.name, boyut: dosya.size, toplamParca: toplam });
      if (d?.bulundu && Number(d.toplamParca) === toplam && d.id) {
        id = d.id;
        gelenler = new Set((d.gelenler || []).map(Number));
      }
    } catch { /* devam ettirilemedi, bastan yuklenir */ }
  }

  if (!id) {
    // Yükleme kimliği toplam parça sayısıyla birlikte açılıyor. Parça boyutu
    // sonradan küçülürse toplam değişir, o yüzden kimlik YENİDEN alınır.
    ({ id } = await tasiyici.basla({
      dosyaAdi: dosya.name,
      toplamParca: toplam,
      boyut: dosya.size,
    }));
  }

  if (tasiyici.ilerleme && gelenler.size) {
    tasiyici.ilerleme(Math.round((gelenler.size / toplam) * 100));
  }

  let sira = 0;
  let ayniBoyutDeneme = 0;

  while (sira < toplam) {
    // TRY'IN DISINDA kontrol ediliyor: icinde olsaydi asagidaki catch onu
    // "belirsiz hata" sayip parca kucultmeye kalkardi.
    if (tasiyici.signal?.aborted) throw new IptalEdildi();

    // Zaten gelmis parca tekrar gonderilmez — devam ettirmenin tum kazanci bu.
    if (gelenler.has(sira)) {
      sira += 1;
      if (tasiyici.ilerleme) tasiyici.ilerleme(Math.round((sira / toplam) * 100));
      continue;
    }

    const bas = sira * parcaBoyutu;
    const parca = dosya.slice(bas, Math.min(bas + parcaBoyutu, dosya.size));
    try {
      await tasiyici.parcaGonder(id, sira, parca);
      sira += 1;
      ayniBoyutDeneme = 0;
      if (tasiyici.ilerleme) tasiyici.ilerleme(Math.round((sira / toplam) * 100));
    } catch (hata) {
      const kod = durumKodu(hata);

      // Kesinlikle boyutla ilgisi olmayan hatada küçültmenin anlamı yok;
      // denemeye devam etmek gerçek sorunu gizler.
      if (kaliciHataMi(kod)) throw hata;

      // Önce aynı boyutta birkaç kez daha dene (geçici kopma varsayımı).
      if (ayniBoyutDeneme < AYNI_BOYUT_DENEME) {
        ayniBoyutDeneme += 1;
        continue;
      }

      // Olmuyorsa parçayı küçült ve BAŞTAN başla: toplam parça sayısı
      // değiştiği için sunucudaki kayıt da yeniden açılmalı.
      const yeniBoyut = sonrakiParcaBoyutu(parcaBoyutu);
      if (yeniBoyut === null) throw hata; // en küçüğe indik, sorun boyut değil

      parcaBoyutu = yeniBoyut;
      toplam = parcaSayisi(dosya.size, parcaBoyutu);
      sira = 0;
      ayniBoyutDeneme = 0;
      // Parca boyutu degisti: eski parcalar artik baska bir bolunmeye ait,
      // ATLANAMAZ. Aksi halde dosya yanlis birlesirdi.
      gelenler = new Set();
      ({ id } = await tasiyici.basla({
        dosyaAdi: dosya.name,
        toplamParca: toplam,
        boyut: dosya.size,
      }));
    }
  }

  if (tasiyici.signal?.aborted) throw new IptalEdildi();
  return await tasiyici.bitir(id);
}

export default parcaliGonder;
